import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardMedia,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Fade,
  Chip
} from '@mui/material';
import {
  Close,
  AutoStories,
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';

interface ExampleImage {
  id: string;
  book: string;
  fileName: string;
  alt: string;
}

interface StyleExample {
  id: string;
  style: string;
  fileName: string;
  alt: string;
  subject: string;
}

const styleExamples: StyleExample[] = [
  // Caterpillar in different styles
  { id: 'caterpillar-anime', style: 'Anime', fileName: 'alice_caterpillar_anime.png', alt: 'Alice Caterpillar - Anime style', subject: 'Caterpillar' },
  { id: 'caterpillar-children', style: 'Children\'s Book', fileName: 'alice_caterpillar_children.png', alt: 'Alice Caterpillar - Children\'s book style', subject: 'Caterpillar' },
  { id: 'caterpillar-cartoon', style: 'Cartoon', fileName: 'alice_caaterpillar_cartoon.png', alt: 'Alice Caterpillar - Cartoon style', subject: 'Caterpillar' },
  { id: 'caterpillar-scaryrealistic', style: 'Scary Realistic', fileName: 'alice_caterpillar_scaryrealistic.png', alt: 'Alice Caterpillar - Scary realistic style', subject: 'Caterpillar' },
  
  // Rabbit in different styles
  { id: 'rabbit-anime', style: 'Anime', fileName: 'alice_rabbit_anime.png', alt: 'White Rabbit - Anime style', subject: 'White Rabbit' },
  { id: 'rabbit-children', style: 'Children\'s Book', fileName: 'alice_rabbit_children.png', alt: 'White Rabbit - Children\'s book style', subject: 'White Rabbit' },
  { id: 'rabbit-cartoon', style: 'Cartoon', fileName: 'alice_rabbit_cartoon.png', alt: 'White Rabbit - Cartoon style', subject: 'White Rabbit' },
  { id: 'rabbit-scaryrealistic', style: 'Scary Realistic', fileName: 'alice_rabbit_scaryrealistic.png', alt: 'White Rabbit - Scary realistic style', subject: 'White Rabbit' },
];

const examples: ExampleImage[] = [
  // Alice in Wonderland
  { id: 'alice-1', book: 'Alice in Wonderland', fileName: 'alice_cat.png', alt: 'Alice in Wonderland - Cheshire Cat scene' },
  { id: 'alice-2', book: 'Alice in Wonderland', fileName: 'alice_caterpillar.png', alt: 'Alice in Wonderland - Caterpillar scene' },
  { id: 'alice-3', book: 'Alice in Wonderland', fileName: 'alice_rabbit.png', alt: 'Alice in Wonderland - White Rabbit scene' },
  
  // Dracula
  { id: 'dracula-1', book: 'Dracula', fileName: 'dracula_count.png', alt: 'Dracula - Count Dracula scene' },
  { id: 'dracula-2', book: 'Dracula', fileName: 'dracula_crawl.png', alt: 'Dracula - Crawling scene' },
  
  // Sherlock Holmes
  { id: 'sherlock-1', book: 'The Adventures of Sherlock Holmes', fileName: 'sherlock_lab.png', alt: 'Sherlock Holmes - Laboratory scene' },
  { id: 'sherlock-2', book: 'The Adventures of Sherlock Holmes', fileName: 'sherlock_caravan.png', alt: 'Sherlock Holmes - Caravan scene' },
  { id: 'sherlock-3', book: 'The Adventures of Sherlock Holmes', fileName: 'sherlock_mountain.png', alt: 'Sherlock Holmes - Mountain scene' },
  { id: 'sherlock-4', book: 'The Adventures of Sherlock Holmes', fileName: 'sherlock_rider.png', alt: 'Sherlock Holmes - Rider scene' },
  { id: 'sherlock-5', book: 'The Adventures of Sherlock Holmes', fileName: 'sherlock_rider_2.png', alt: 'Sherlock Holmes - Rider scene 2' },
  
  // Grimm Fairy Tales
  { id: 'grimm-1', book: 'Grimm Fairy Tales', fileName: 'grimm_bird.png', alt: 'Grimm Fairy Tales - Bird scene' },
  { id: 'grimm-2', book: 'Grimm Fairy Tales', fileName: 'grimm_bird_2.png', alt: 'Grimm Fairy Tales - Bird scene 2' },
  { id: 'grimm-3', book: 'Grimm Fairy Tales', fileName: 'grimm_dog.png', alt: 'Grimm Fairy Tales - Dog scene' },
  { id: 'grimm-4', book: 'Grimm Fairy Tales', fileName: 'grimm_house.png', alt: 'Grimm Fairy Tales - House scene' },
  { id: 'grimm-5', book: 'Grimm Fairy Tales', fileName: 'grimm_turnip.png', alt: 'Grimm Fairy Tales - Turnip scene' },
  
  // Around the World in 80 Days
  { id: '80days-1', book: 'Around the World in 80 Days', fileName: '80days_boat2.png', alt: 'Around the World in 80 Days - Boat scene' },
  { id: '80days-2', book: 'Around the World in 80 Days', fileName: '80days_elephant2.png', alt: 'Around the World in 80 Days - Elephant scene' },
  { id: '80days-3', book: 'Around the World in 80 Days', fileName: '80days_mongolia2.png', alt: 'Around the World in 80 Days - Mongolia scene' },
  { id: '80days-4', book: 'Around the World in 80 Days', fileName: '80days_saddlepeak2.png', alt: 'Around the World in 80 Days - Saddle Peak scene' },
  { id: '80days-5', book: 'Around the World in 80 Days', fileName: '80days_youngwoman2.png', alt: 'Around the World in 80 Days - Young woman scene' },
  
  // The Odyssey
  { id: 'odyssey-1', book: 'The Odyssey', fileName: 'odyssey_helen2.png', alt: 'The Odyssey - Helen scene' },
  { id: 'odyssey-2', book: 'The Odyssey', fileName: 'odyssey_mentes.png', alt: 'The Odyssey - Mentes scene' },
  { id: 'odyssey-3', book: 'The Odyssey', fileName: 'odyssey_penelope2.png', alt: 'The Odyssey - Penelope scene' },
  { id: 'odyssey-4', book: 'The Odyssey', fileName: 'odyssey_telemaches3.png', alt: 'The Odyssey - Telemachus scene' },
  { id: 'odyssey-5', book: 'The Odyssey', fileName: 'odyssey_telemaches4.png', alt: 'The Odyssey - Telemachus scene 2' },
  
  // Peter Pan
  { id: 'peterpan-1', book: 'Peter Pan', fileName: 'peterpan_croc2.png', alt: 'Peter Pan - Crocodile scene' },
  { id: 'peterpan-2', book: 'Peter Pan', fileName: 'peterpan_mermaids2.png', alt: 'Peter Pan - Mermaids scene' },
  { id: 'peterpan-3', book: 'Peter Pan', fileName: 'peterpan_mrsdarling2.png', alt: 'Peter Pan - Mrs. Darling scene' },
  { id: 'peterpan-4', book: 'Peter Pan', fileName: 'peterpan_neverland.png', alt: 'Peter Pan - Neverland scene' },
  { id: 'peterpan-5', book: 'Peter Pan', fileName: 'peterpan_tie.png', alt: 'Peter Pan - Tie scene' },
  { id: 'peterpan-6', book: 'Peter Pan', fileName: 'peterpan_tinkerbell2.png', alt: 'Peter Pan - Tinker Bell scene' },
  { id: 'peterpan-7', book: 'Peter Pan', fileName: 'peterpan_wendy2.png', alt: 'Peter Pan - Wendy scene' },
  
  // War of the Worlds
  { id: 'waroftheworlds-1', book: 'War of the Worlds', fileName: 'waroftheworlds_artillery2.png', alt: 'War of the Worlds - Artillery scene' },
  { id: 'waroftheworlds-2', book: 'War of the Worlds', fileName: 'waroftheworlds_houses_2.png', alt: 'War of the Worlds - Houses scene' },
  { id: 'waroftheworlds-3', book: 'War of the Worlds', fileName: 'waroftheworlds_machines2.png', alt: 'War of the Worlds - Machines scene' },
  { id: 'waroftheworlds-4', book: 'War of the Worlds', fileName: 'waroftheworlds_whiteflag2.png', alt: 'War of the Worlds - White flag scene' },
];

const ExamplesPage: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(null);
  
  // Combine all images for the lightbox
  const allImages = [...examples, ...styleExamples.map(s => ({ 
    id: s.id, 
    book: `Style: ${s.style}`, 
    fileName: s.fileName, 
    alt: s.alt 
  }))];

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const handleClose = () => {
    setCurrentImageIndex(null);
  };

  const handleNext = () => {
    if (currentImageIndex !== null) {
      setCurrentImageIndex((currentImageIndex + 1) % allImages.length);
    }
  };

  const handlePrev = () => {
    if (currentImageIndex !== null) {
      setCurrentImageIndex((currentImageIndex - 1 + allImages.length) % allImages.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (currentImageIndex === null) return;
      
      if (e.key === 'ArrowRight') {
        setCurrentImageIndex((currentImageIndex + 1) % allImages.length);
      } else if (e.key === 'ArrowLeft') {
        setCurrentImageIndex((currentImageIndex - 1 + allImages.length) % allImages.length);
      } else if (e.key === 'Escape') {
        setCurrentImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, allImages.length]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Examples
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
          See how TextWiz brings books to life with AI-generated images
        </Typography>
        <Chip
          icon={<AutoStories />}
          label="All examples from Project Gutenberg classics"
          sx={{
            backgroundColor: 'info.main',
            color: '#fff',
            fontWeight: 500
          }}
        />
      </Box>

      {/* Style Variations Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 1, fontWeight: 600, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
          Style Variations
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, fontSize: { xs: '0.9rem', md: '1rem' } }}>
          See how the same scene can be visualized in different artistic styles
        </Typography>
        
        {/* Caterpillar Styles */}
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 500 }}>
          Caterpillar Scene
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {styleExamples.filter(ex => ex.subject === 'Caterpillar').map((image, index) => (
            <Grid item xs={12} sm={6} md={3} key={image.id}>
              <Fade in timeout={300 + (index * 50)}>
                <Card 
                  sx={{
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => handleImageClick(examples.length + styleExamples.indexOf(image))}
                >
                  <CardMedia
                    component="img"
                    height="300"
                    image={`/examples/${image.fileName}`}
                    alt={image.alt}
                    sx={{
                      objectFit: 'cover',
                      backgroundColor: 'action.hover'
                    }}
                  />
                  <Box sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
                    <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', fontWeight: 600 }}>
                      {image.style}
                    </Typography>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Rabbit Styles */}
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 500 }}>
          White Rabbit Scene
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {styleExamples.filter(ex => ex.subject === 'White Rabbit').map((image, index) => (
            <Grid item xs={12} sm={6} md={3} key={image.id}>
              <Fade in timeout={300 + (index * 50)}>
                <Card 
                  sx={{
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => handleImageClick(examples.length + styleExamples.indexOf(image))}
                >
                  <CardMedia
                    component="img"
                    height="300"
                    image={`/examples/${image.fileName}`}
                    alt={image.alt}
                    sx={{
                      objectFit: 'cover',
                      backgroundColor: 'action.hover'
                    }}
                  />
                  <Box sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
                    <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', fontWeight: 600 }}>
                      {image.style}
                    </Typography>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Book Examples Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 3, fontWeight: 600, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
          Book Examples
        </Typography>
      </Box>

      {/* Image Gallery */}
      <Grid container spacing={3}>
        {examples.map((image, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
            <Fade in timeout={300 + (index * 50)}>
              <Card 
                sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main'
                  }
                }}
                onClick={() => handleImageClick(index)}
              >
                <CardMedia
                  component="img"
                  height="300"
                  image={`/examples/${image.fileName}`}
                  alt={image.alt}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: 'action.hover'
                  }}
                />
                <Box sx={{ p: 1.5, backgroundColor: 'background.paper' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {image.book}
                  </Typography>
                </Box>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {/* Image Preview Dialog */}
      <Dialog
        open={currentImageIndex !== null}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: '95vh'
          }
        }}
      >
        {currentImageIndex !== null && (
          <>
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                zIndex: 1
              }}
            >
              <Close />
            </IconButton>

            {/* Previous Button */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                zIndex: 1
              }}
            >
              <ArrowBackIos sx={{ ml: 0.5 }} />
            </IconButton>

            {/* Next Button */}
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                zIndex: 1
              }}
            >
              <ArrowForwardIos />
            </IconButton>
            
            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                <img
                  src={`/examples/${allImages[currentImageIndex].fileName}`}
                  alt={allImages[currentImageIndex].alt}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    objectFit: 'contain'
                  }}
                />
                {/* Image Info */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                    {allImages[currentImageIndex].book}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>
                    {currentImageIndex + 1} / {allImages.length}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ExamplesPage;

